import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import GenericButton from "../Button/GenericButton";
import { ReactComponent as IconCancel } from "../../shared/assets/Icons/IconCrossSquare.svg";

/* ──────────────────────────────────────────────────────────────────────────────
 * Types
 * ──────────────────────────────────────────────────────────────────────────── */

export interface MultiFilterFieldOption {
	id: string;
	label: string;
	count?: number;
}

export interface MultiFilterFieldConfig {
	/** Stable id used as key in {@link MultiFilterValue}. */
	id: string;
	/** Section title inside the card. */
	label: string;
	options: MultiFilterFieldOption[];
	/** When true (default), shows an inline search to narrow the option list. */
	searchable?: boolean;
	searchPlaceholder?: string;
	emptyOptionsMessage?: string;
}

/** Applied filter state: for each field id, the list of selected option ids. */
export type MultiFilterValue = Record<string, string[]>;

export interface MultiFilterDropdownProps {
	fields: MultiFilterFieldConfig[];
	/** Controlled applied state. Missing or empty arrays mean "no filter for that field". */
	value: MultiFilterValue;
	/**
	 * Called whenever the applied state changes:
	 * - On Apply (commits the card draft for every field).
	 * - On instant chip removal from the trigger applied-chips bar.
	 */
	onApply: (next: MultiFilterValue) => void;
	/** Text shown inside the trigger button. Default `"Filters"`. */
	triggerLabel?: string;
	/**
	 * What the clear action applies. Defaults to an empty selection on every
	 * field — "show everything".
	 *
	 * A view that OPENS on a filter needs the other meaning: clearing should
	 * put the default back, not strip it, or the default becomes unreachable
	 * once the user changes it (CTX-523). Pass that default here and the
	 * action resets to it, and hides itself while the selection already
	 * matches it.
	 */
	resetValue?: MultiFilterValue;
	/** Label of the clear action. Default `"Clear filters"`. */
	clearLabel?: string;
	/** Title at the top of the card. Defaults to `triggerLabel`. */
	cardTitle?: string;
	cardDescription?: string;
	className?: string;
}

/* ──────────────────────────────────────────────────────────────────────────────
 * Helpers
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Whether two selections pick the same options, ignoring the order of fields
 * and of ids within a field — a missing key and an empty array both mean
 * "nothing selected here".
 */
function sameSelection(
	fields: MultiFilterFieldConfig[],
	a: MultiFilterValue,
	b: MultiFilterValue
): boolean {
	for (const field of fields) {
		const left = [...(a[field.id] ?? [])].sort();
		const right = [...(b[field.id] ?? [])].sort();
		if (left.length !== right.length) return false;
		if (left.some((id, index) => id !== right[index])) return false;
	}
	return true;
}

function orderSelection(
	selected: Set<string>,
	options: MultiFilterFieldOption[]
): string[] {
	const out: string[] = [];
	for (const o of options) if (selected.has(o.id)) out.push(o.id);
	return out;
}

function appliedOptionsByField(
	fields: MultiFilterFieldConfig[],
	value: MultiFilterValue
): { field: MultiFilterFieldConfig; options: MultiFilterFieldOption[] }[] {
	return fields.map((f) => {
		const ids = new Set(value[f.id] ?? []);
		const options = f.options.filter((o) => ids.has(o.id));
		return { field: f, options };
	});
}

/* ──────────────────────────────────────────────────────────────────────────────
 * Component
 * ──────────────────────────────────────────────────────────────────────────── */

export default function MultiFilterDropdown({
	fields,
	value,
	onApply,
	triggerLabel = "Filters",
	resetValue,
	clearLabel,
	cardTitle,
	cardDescription,
	className,
}: MultiFilterDropdownProps) {
	// Left undefaulted above so callers that pass nothing keep the exact
	// accessible name they had before this became configurable.
	const clearText = clearLabel ?? "Clear filters";
	const clearAria = clearLabel ?? "Clear all applied filters";
	const [cardOpen, setCardOpen] = useState(false);
	const [draft, setDraft] = useState<Record<string, Set<string>>>({});
	const [searchByField, setSearchByField] = useState<Record<string, string>>({});

	const rootRef = useRef<HTMLDivElement>(null);

	const effectiveCardTitle = cardTitle ?? triggerLabel;
	const appliedByField = useMemo(
		() => appliedOptionsByField(fields, value),
		[fields, value]
	);

	const openCard = useCallback(() => {
		const seedDraft: Record<string, Set<string>> = {};
		const seedSearch: Record<string, string> = {};
		for (const f of fields) {
			seedDraft[f.id] = new Set(value[f.id] ?? []);
			seedSearch[f.id] = "";
		}
		setDraft(seedDraft);
		setSearchByField(seedSearch);
		setCardOpen(true);
	}, [fields, value]);

	const closeCardDiscard = useCallback(() => {
		setCardOpen(false);
	}, []);

	const applyDraft = useCallback(() => {
		const next: MultiFilterValue = {};
		for (const f of fields) {
			const sel = draft[f.id] ?? new Set<string>();
			next[f.id] = orderSelection(sel, f.options);
		}
		onApply(next);
		setCardOpen(false);
	}, [draft, fields, onApply]);

	const clearAppliedField = useCallback(
		(fieldId: string) => {
			const next: MultiFilterValue = { ...value };
			next[fieldId] = [];
			onApply(next);
		},
		[onApply, value]
	);

	/** Selection the clear action restores: the caller's default, or nothing. */
	const resetTarget = useMemo(() => {
		if (resetValue) return resetValue;
		const cleared: MultiFilterValue = {};
		for (const f of fields) cleared[f.id] = [];
		return cleared;
	}, [resetValue, fields]);

	const clearAllApplied = useCallback(() => {
		onApply(resetTarget);
	}, [onApply, resetTarget]);

	// Without a `resetValue` this is exactly "something is applied", which is
	// what gated the action before — every existing caller behaves as it did.
	const canReset = useMemo(
		() => !sameSelection(fields, value, resetTarget),
		[fields, value, resetTarget]
	);

	const toggleDraft = (fieldId: string, optionId: string) => {
		setDraft((prev) => {
			const current = new Set(prev[fieldId] ?? []);
			if (current.has(optionId)) current.delete(optionId);
			else current.add(optionId);
			return { ...prev, [fieldId]: current };
		});
	};

	const removeDraftChip = (fieldId: string, optionId: string) => {
		setDraft((prev) => {
			const current = new Set(prev[fieldId] ?? []);
			current.delete(optionId);
			return { ...prev, [fieldId]: current };
		});
	};

	useEffect(() => {
		if (!cardOpen) return;
		const onPointerDown = (e: MouseEvent | TouchEvent) => {
			const el = rootRef.current;
			if (!el) return;
			const t = e.target;
			if (t instanceof Node && !el.contains(t)) closeCardDiscard();
		};
		document.addEventListener("mousedown", onPointerDown);
		document.addEventListener("touchstart", onPointerDown);
		return () => {
			document.removeEventListener("mousedown", onPointerDown);
			document.removeEventListener("touchstart", onPointerDown);
		};
	}, [cardOpen, closeCardDiscard]);

	useEffect(() => {
		if (!cardOpen) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") closeCardDiscard();
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [cardOpen, closeCardDiscard]);

	const filteredOptionsByField = useMemo(() => {
		const out: Record<string, MultiFilterFieldOption[]> = {};
		for (const f of fields) {
			const q = (searchByField[f.id] ?? "").trim().toLowerCase();
			out[f.id] = q
				? f.options.filter((o) => o.label.toLowerCase().includes(q))
				: f.options;
		}
		return out;
	}, [fields, searchByField]);

	const draftOrderedByField = useMemo(() => {
		const out: Record<string, MultiFilterFieldOption[]> = {};
		for (const f of fields) {
			const sel = draft[f.id] ?? new Set<string>();
			const ids = orderSelection(sel, f.options);
			const byId = new Map(f.options.map((o) => [o.id, o]));
			out[f.id] = ids.map((id) => byId.get(id)).filter(Boolean) as MultiFilterFieldOption[];
		}
		return out;
	}, [draft, fields]);

	const appliedChipSummary = useMemo(() => {
		const summary: {
			fieldId: string;
			fieldLabel: string;
			firstOptionLabel: string;
			extraLabels: string[];
			count: number;
		}[] = [];
		for (const entry of appliedByField) {
			if (entry.options.length === 0) continue;
			const [first, ...rest] = entry.options;
			summary.push({
				fieldId: entry.field.id,
				fieldLabel: entry.field.label,
				firstOptionLabel: first.label,
				extraLabels: rest.map((o) => o.label),
				count: entry.options.length,
			});
		}
		return summary;
	}, [appliedByField]);

	const onTriggerActivate = () => {
		if (cardOpen) closeCardDiscard();
		else openCard();
	};

	return (
		<div
			className={`flex w-full items-center justify-between gap-3 ${className ?? ""}`}
		>
			<div
				ref={rootRef}
				className="relative flex min-w-0 max-w-md flex-1 items-start"
			>
				<div className="min-w-0 flex-1">
					<div
						role="button"
						tabIndex={0}
						onClick={onTriggerActivate}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								onTriggerActivate();
							}
						}}
						aria-expanded={cardOpen}
						aria-haspopup="dialog"
						className="flex w-full min-w-0 cursor-pointer items-start gap-2 rounded-md border border-semantic-border-primary bg-semantic-bg-primary px-2.5 py-1.5 text-left text-xs font-medium text-semantic-text-primary shadow-sm transition-colors hover:bg-semantic-state-active"
					>
						<div
							className="flex h-6 min-w-0 flex-1 flex-nowrap content-start items-center gap-1.5 overflow-x-auto overflow-y-hidden pr-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
							onWheel={(e) => {
								if (e.deltaY !== 0 && e.deltaX === 0) {
									e.currentTarget.scrollLeft += e.deltaY;
								}
							}}
						>
							<span className="shrink-0 text-semantic-text-secondary">
								{triggerLabel}
							</span>
							{appliedChipSummary.map((chip) => {
								const tooltipLabel =
									chip.count > 1
										? `${chip.fieldLabel}: ${chip.firstOptionLabel}, ${chip.extraLabels.join(", ")}`
										: `${chip.fieldLabel}: ${chip.firstOptionLabel}`;
								return (
									<span
										key={chip.fieldId}
										className="inline-flex max-w-full items-center gap-1 rounded-full border border-semantic-border-primary bg-semantic-bg-secondary px-2 py-0.5 text-[11px] font-medium text-semantic-text-primary"
										title={tooltipLabel}
									>
										<span className="truncate">
											<span className="text-semantic-text-secondary">
												{chip.fieldLabel}:{" "}
											</span>
											<span>{chip.firstOptionLabel}</span>
										</span>
										{chip.count > 1 && (
											<span
												aria-label={`and ${chip.count - 1} more`}
												className="shrink-0 text-semantic-text-secondary"
											>
												…
											</span>
										)}
										<button
											type="button"
											onClick={(e) => {
												e.stopPropagation();
												clearAppliedField(chip.fieldId);
											}}
											onKeyDown={(e) => e.stopPropagation()}
											className="shrink-0 rounded p-0.5 text-semantic-text-secondary hover:bg-semantic-state-active hover:text-semantic-text-primary"
											aria-label={`Clear ${chip.fieldLabel} filter`}
										>
											<IconCancel className="h-3 w-3" />
										</button>
									</span>
								);
							})}
						</div>
						<span
							className="shrink-0 self-center text-semantic-text-tertiary"
							aria-hidden
						>
							▾
						</span>
					</div>

					{cardOpen && (
						<div
							role="dialog"
							aria-label={effectiveCardTitle}
							className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[min(28rem,calc(100vh-8rem))] overflow-hidden rounded-lg border border-semantic-border-primary bg-semantic-bg-primary shadow-lg"
						>
							<div className="border-b border-semantic-border-primary px-3 py-2">
								<p className="text-xs font-semibold text-semantic-text-primary">
									{effectiveCardTitle}
								</p>
								{cardDescription && (
									<p className="mt-0.5 text-[11px] text-semantic-text-secondary">
										{cardDescription}
									</p>
								)}
							</div>

							<div className="max-h-[20rem] overflow-y-auto">
								{fields.length === 0 ? (
									<p className="px-3 py-3 text-xs text-semantic-text-tertiary">
										No filters available.
									</p>
								) : (
									<ul className="divide-y divide-semantic-border-primary">
										{fields.map((f) => {
											const searchable = f.searchable ?? true;
											const draftChips = draftOrderedByField[f.id] ?? [];
											const filteredOptions =
												filteredOptionsByField[f.id] ?? [];
											const draftSet = draft[f.id] ?? new Set<string>();
											return (
												<li key={f.id} className="space-y-2 px-3 py-3">
													<p className="text-[11px] font-semibold uppercase tracking-wide text-semantic-text-secondary">
														{f.label}
													</p>

													{searchable && (
														<div className="flex min-h-[2.25rem] flex-wrap items-center gap-1.5 rounded-md border border-semantic-border-primary bg-semantic-bg-secondary px-2 py-1.5">
															{draftChips.map((opt) => (
																<span
																	key={opt.id}
																	className="inline-flex max-w-full items-center gap-1 rounded-full border border-semantic-border-primary bg-semantic-bg-primary px-2 py-0.5 text-[11px] font-medium text-semantic-text-primary"
																>
																	<span
																		className="truncate"
																		title={opt.label}
																	>
																		{opt.label}
																	</span>
																	<button
																		type="button"
																		onClick={() =>
																			removeDraftChip(f.id, opt.id)
																		}
																		className="shrink-0 rounded p-0.5 text-semantic-text-secondary hover:bg-semantic-state-active hover:text-semantic-text-primary"
																		aria-label={`Remove ${opt.label} from ${f.label} selection`}
																	>
																		<IconCancel className="h-3 w-3" />
																	</button>
																</span>
															))}
															<input
																type="search"
																value={searchByField[f.id] ?? ""}
																onChange={(e) =>
																	setSearchByField((prev) => ({
																		...prev,
																		[f.id]: e.target.value,
																	}))
																}
																placeholder={
																	f.searchPlaceholder ?? "Search…"
																}
																className="min-w-[6rem] flex-1 border-0 bg-transparent text-xs text-semantic-text-primary outline-none placeholder:text-semantic-text-tertiary"
																autoComplete="off"
															/>
														</div>
													)}

													<ul className="max-h-44 space-y-0.5 overflow-y-auto rounded-md border border-semantic-border-primary p-1">
														{filteredOptions.length === 0 ? (
															<li className="px-2 py-2 text-xs text-semantic-text-tertiary">
																{f.emptyOptionsMessage ??
																	"No matching options"}
															</li>
														) : (
															filteredOptions.map((opt) => {
																const checked = draftSet.has(opt.id);
																return (
																	<li key={opt.id}>
																		<label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-semantic-bg-tertiary">
																			<input
																				type="checkbox"
																				checked={checked}
																				onChange={() =>
																					toggleDraft(f.id, opt.id)
																				}
																				className="h-3.5 w-3.5 shrink-0 rounded border-semantic-border-primary"
																			/>
																			<span className="min-w-0 flex-1 truncate text-xs font-medium text-semantic-text-primary">
																				{opt.label}
																			</span>
																			{opt.count != null && (
																				<span className="shrink-0 text-[11px] tabular-nums text-semantic-text-secondary">
																					({opt.count})
																				</span>
																			)}
																		</label>
																	</li>
																);
															})
														)}
													</ul>
												</li>
											);
										})}
									</ul>
								)}
							</div>

							<div className="flex justify-end gap-2 border-t border-semantic-border-primary px-3 py-2">
								<GenericButton
									variant="secondary"
									size="sm"
									type="button"
									onClick={closeCardDiscard}
									label="Cancel"
								/>
								<GenericButton
									variant="brand"
									size="sm"
									type="button"
									onClick={applyDraft}
									label="Apply"
								/>
							</div>
						</div>
					)}
				</div>
			</div>

			{canReset && (
				<button
					type="button"
					onClick={clearAllApplied}
					className="inline-flex shrink-0 items-center gap-1 self-center text-xs font-medium text-semantic-text-success hover:underline"
					aria-label={clearAria}
				>
					<IconCancel className="h-3 w-3" />
					<span>{clearText}</span>
				</button>
			)}
		</div>
	);
}
